// ============================================================================
// Tour schedule — shared next-occurrence computation and display formatting.
// ----------------------------------------------------------------------------
// A tour's schedule is a list of TourAvailability blocks: (startDayOfWeek at
// startTime) → (endDayOfWeek at endTime), where days use the KST numbering
// (1 = Sunday .. 7 = Saturday) and times are milliseconds since local midnight,
// all wall-clock in the tour's IANA time zone.
//
// computeNextOccurrence mirrors the server's occurrence materialization
// (GuardTourService.updateGuardToursFromTour) exactly: the END of the window is
// anchored first (wrapping +7 days when it is already past), the START is
// derived from it and normalized to sit within (end - 7d, end], day arithmetic
// is done on wall-clock datetimes, and the results are converted to absolute
// instants through the tour's zone so DST is honored. One deliberate
// divergence, per the 2026-07-02 decision record: the window is treated as
// half-open [start, end) — at the exact end instant the occurrence is over.
//
// All zone math uses Intl.DateTimeFormat with an explicit timeZone (no deps),
// like the advisory schedule helpers this vocabulary is shared with.
// ============================================================================

import { zoneShortName } from '../advisory/advisorySchedule';
import { browserZoneId } from '../advisory/advisoryValidation';

/** One availability block as returned by the API (local wall-clock fields). */
export interface TourScheduleBlock {
  /** KST day of week: 1 = Sunday .. 7 = Saturday. */
  startDayOfWeek: number | null;
  /** Milliseconds since local midnight. */
  startTime: number | null;
  endDayOfWeek: number | null;
  endTime: number | null;
}

/** A tour's schedule as returned by guardtour/schedule/list and tour/search. */
export interface TourScheduleData {
  tourId: number;
  /** Stable identity across tour revisions; join key for guard-tour list rows. */
  revisionGroup: number;
  title: string;
  timeZoneId: string | null;
  availabilities: TourScheduleBlock[];
}

/** A concrete occurrence window, as absolute instants. */
export interface TourOccurrence {
  start: Date;
  end: Date;
}

export interface TourScheduleState {
  /** 'none' when the schedule has no valid blocks. */
  status: 'in-progress' | 'upcoming' | 'none';
  /** Start of the relevant occurrence (the current one when in progress). */
  start: Date | null;
  /** End of the relevant occurrence. */
  end: Date | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

// KST day numbering (1=Sun..7=Sat) → labels. Index 0 unused.
const KST_DAY_SHORT = ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const KST_DAY_LONG = ['', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Intl short weekday → KST day number.
const WEEKDAY_TO_KST: Record<string, number> = {
  SUN: 1, MON: 2, TUE: 3, WED: 4, THU: 5, FRI: 6, SAT: 7,
};

interface ZoneParts {
  year: number;
  month: number; // 1-12
  day: number;
  /** KST day of week (1=Sun..7=Sat). */
  kstDayOfWeek: number;
  /** Milliseconds since local midnight (second precision). */
  msOfDay: number;
}

/** The wall-clock components of an instant, as rendered in `timeZone`. */
function zonePartsOf(instant: Date, timeZone: string): ZoneParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
  });
  const map: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return {
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
    kstDayOfWeek: WEEKDAY_TO_KST[(map.weekday || '').toUpperCase()] || 0,
    msOfDay:
      parseInt(map.hour, 10) * 3600000 +
      parseInt(map.minute, 10) * 60000 +
      parseInt(map.second, 10) * 1000,
  };
}

/**
 * A wall-clock datetime, kept as a calendar date (proleptic UTC day index, so
 * day arithmetic is trivial) plus milliseconds since midnight. Comparisons and
 * differences on `wallMs` match java.time.LocalDateTime semantics: pure
 * wall-clock, DST-blind — which is exactly how the server normalizes the
 * start against the end.
 */
interface WallDateTime {
  /** Date.UTC(y, m-1, d) — midnight of the calendar date, as a UTC timestamp. */
  dateMs: number;
  msOfDay: number;
}

function wallMs(w: WallDateTime): number {
  return w.dateMs + w.msOfDay;
}

function plusDays(w: WallDateTime, days: number): WallDateTime {
  return { dateMs: w.dateMs + days * DAY_MS, msOfDay: w.msOfDay };
}

/**
 * The UTC instant whose wall-clock representation in `timeZone` is the given
 * wall datetime. Format-and-correct-by-offset, iterated so instants adjacent
 * to a DST transition resolve with the correct side's offset (a single pass
 * picks the wrong offset for wall times within one offset-width of the
 * transition). Ambiguous wall times (fall-back) resolve to the earlier
 * offset and nonexistent ones (spring-forward gap) adjust forward — both
 * matching java.time's ZonedDateTime resolution on the server.
 */
function wallDateTimeToUtc(w: WallDateTime, timeZone: string): Date {
  const target = w.dateMs + w.msOfDay;
  const wallUtcAt = (t: number): number => {
    const p = zonePartsOf(new Date(t), timeZone);
    return Date.UTC(p.year, p.month - 1, p.day) + p.msOfDay;
  };
  const t1 = target + (target - wallUtcAt(target));
  if (wallUtcAt(t1) === target) return new Date(t1);
  const t2 = target + (t1 - wallUtcAt(t1));
  if (wallUtcAt(t2) === target) return new Date(t2);
  return new Date(t1); // DST gap: no instant has this wall time; t1 is the forward-adjusted one
}

function isValidBlock(block: TourScheduleBlock): boolean {
  const validDay = (d: number | null): boolean => d != null && d >= 1 && d <= 7;
  const validTime = (t: number | null): boolean => t != null && t >= 0 && t < DAY_MS;
  return (
    validDay(block.startDayOfWeek) &&
    validDay(block.endDayOfWeek) &&
    validTime(block.startTime) &&
    validTime(block.endTime)
  );
}

/**
 * The single next (or currently ongoing) occurrence of one availability block,
 * relative to `now`. Mirrors updateGuardToursFromTour; half-open [start, end).
 * Returns null for incomplete/invalid blocks or an unusable zone.
 */
export function computeNextOccurrence(
  block: TourScheduleBlock,
  zoneId: string | null,
  now: Date
): TourOccurrence | null {
  if (!isValidBlock(block)) return null;
  const zone = zoneId || 'UTC';

  let nowLocal: ZoneParts;
  try {
    nowLocal = zonePartsOf(now, zone);
  } catch {
    return null;
  }
  const nowDate = Date.UTC(nowLocal.year, nowLocal.month - 1, nowLocal.day);

  let start: WallDateTime = { dateMs: nowDate, msOfDay: block.startTime as number };
  let end: WallDateTime = { dateMs: nowDate, msOfDay: block.endTime as number };

  // Anchor the END: in the future (or ongoing) as-is, else wrap to next week.
  // Server uses `endTime >= nowTime` on the equal-day case; half-open [start,
  // end) makes that strict — at the exact end instant the window has closed.
  const endDay = block.endDayOfWeek as number;
  const startDay = block.startDayOfWeek as number;
  let daysToAddForEnd: number;
  if (
    endDay > nowLocal.kstDayOfWeek ||
    (endDay === nowLocal.kstDayOfWeek && (block.endTime as number) > nowLocal.msOfDay)
  ) {
    daysToAddForEnd = endDay - nowLocal.kstDayOfWeek;
  } else {
    daysToAddForEnd = endDay - nowLocal.kstDayOfWeek + 7;
  }
  end = plusDays(end, daysToAddForEnd);

  // Derive the START from its day-of-week, then normalize into (end - 7d, end].
  start = plusDays(start, startDay - nowLocal.kstDayOfWeek);
  if (wallMs(start) > wallMs(end)) {
    start = plusDays(start, -7);
  } else if (wallMs(end) - wallMs(start) > WEEK_MS) {
    start = plusDays(start, 7);
  }

  try {
    return {
      start: wallDateTimeToUtc(start, zone),
      end: wallDateTimeToUtc(end, zone),
    };
  } catch {
    return null;
  }
}

/**
 * The live schedule state of a whole tour (all blocks): in progress now, or
 * the soonest upcoming occurrence. One state per tour, never per block —
 * tours with several same-day windows must not repeat.
 */
export function computeTourScheduleState(
  blocks: TourScheduleBlock[],
  zoneId: string | null,
  now: Date
): TourScheduleState {
  const occurrences = (blocks || [])
    .map((b) => computeNextOccurrence(b, zoneId, now))
    .filter((o): o is TourOccurrence => o != null);
  if (occurrences.length === 0) return { status: 'none', start: null, end: null };

  const nowMs = now.getTime();
  const inProgress = occurrences.filter(
    (o) => o.start.getTime() <= nowMs && nowMs < o.end.getTime()
  );
  if (inProgress.length > 0) {
    // Available until the furthest end among currently open windows.
    const current = inProgress.reduce((a, b) => (b.end > a.end ? b : a));
    return { status: 'in-progress', start: current.start, end: current.end };
  }

  const next = occurrences.reduce((a, b) => (b.start < a.start ? b : a));
  return { status: 'upcoming', start: next.start, end: next.end };
}

// ============================================================================
// Formatting
// ============================================================================

/** ms since midnight → "6:00 PM" (wall-clock, no zone involved). */
export function formatKstTimeOfDay(msOfDay: number): string {
  const totalMinutes = Math.floor(msOfDay / 60000);
  const h24 = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const ampm = h24 < 12 ? 'AM' : 'PM';
  return `${h12}:${minute < 10 ? '0' : ''}${minute} ${ampm}`;
}

/** An instant's time of day rendered in a specific zone → "6:00 PM". */
export function formatTimeOfDayInZone(instant: Date, zoneId: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zoneId,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(instant);
  } catch {
    return '';
  }
}

export interface DayLabel {
  /** 'today' | 'tomorrow' | null (further out). */
  relative: string | null;
  weekdayShort: string; // "Tue"
  weekdayLong: string; // "Tuesday"
  daysAway: number;
}

/**
 * How an instant's calendar day relates to now's, both read in `zoneId`
 * ("today", "tomorrow", or just the weekday).
 */
export function dayLabelInZone(instant: Date, zoneId: string, now: Date): DayLabel {
  let zone = zoneId || browserZoneId();
  let p: ZoneParts;
  let n: ZoneParts;
  try {
    p = zonePartsOf(instant, zone);
    n = zonePartsOf(now, zone);
  } catch {
    zone = browserZoneId();
    p = zonePartsOf(instant, zone);
    n = zonePartsOf(now, zone);
  }
  const daysAway = Math.round(
    (Date.UTC(p.year, p.month - 1, p.day) - Date.UTC(n.year, n.month - 1, n.day)) / DAY_MS
  );
  return {
    relative: daysAway === 0 ? 'today' : daysAway === 1 ? 'tomorrow' : null,
    weekdayShort: KST_DAY_SHORT[p.kstDayOfWeek] || '',
    weekdayLong: KST_DAY_LONG[p.kstDayOfWeek] || '',
    daysAway,
  };
}

/**
 * Advisory-pill vocabulary for a next opening, in the VIEWER's zone:
 * "today 6:00 PM" / "tomorrow 7:00 AM" / "Tue 8:00 AM".
 */
export function formatNextOpening(instant: Date, now: Date, withAt = false): string {
  const zone = browserZoneId();
  const label = dayLabelInZone(instant, zone, now);
  const time = formatTimeOfDayInZone(instant, zone);
  const day = label.relative ?? label.weekdayShort;
  return withAt ? `${day} at ${time}` : `${day} ${time}`;
}

/** Zone abbreviation ("CST") when the tour's zone differs from the viewer's. */
export function zoneSuffixIfForeign(zoneId: string | null, at: Date): string {
  if (!zoneId || zoneId === browserZoneId()) return '';
  const short = zoneShortName(zoneId, at);
  return short ? ` (${short})` : '';
}

// --- Schedule line ("Mon–Sat · 6:00 PM – 6:00 AM (CST)") -------------------

/**
 * Day phrase for a set of KST start days: "Every day", "Weekdays", "Weekends",
 * a contiguous range ("Mon–Sat"), or a comma list ("Mon, Wed, Fri").
 */
export function formatKstDays(days: number[]): string {
  const uniq = Array.from(new Set(days.filter((d) => d >= 1 && d <= 7))).sort((a, b) => a - b);
  if (uniq.length === 0) return '';
  if (uniq.length === 7) return 'Every day';
  const isSet = (want: number[]) =>
    uniq.length === want.length && want.every((d, i) => uniq[i] === d);
  if (isSet([2, 3, 4, 5, 6])) return 'Weekdays';
  if (isSet([1, 7])) return 'Weekends';
  const contiguous = uniq.every((d, i) => i === 0 || d === uniq[i - 1] + 1);
  if (contiguous && uniq.length >= 3) {
    return `${KST_DAY_SHORT[uniq[0]]}–${KST_DAY_SHORT[uniq[uniq.length - 1]]}`;
  }
  return uniq.map((d) => KST_DAY_SHORT[d]).join(', ');
}

/**
 * Compact one-line schedule for a tour's availability blocks, in the TOUR's
 * zone (wall-clock times as configured; zone abbreviation appended when it
 * differs from the viewer's). Blocks sharing the same times and day span are
 * grouped into one phrase ("Mon–Sat · 6:00 PM – 6:00 AM"); heterogeneous
 * schedules fall back to listing the groups.
 */
export function formatScheduleLine(
  blocks: TourScheduleBlock[],
  zoneId: string | null,
  now: Date
): string {
  const valid = (blocks || []).filter(isValidBlock);
  if (valid.length === 0) return '';

  // Group by identical (startTime, endTime, day span).
  const groups = new Map<string, { startDays: number[]; startTime: number; endTime: number }>();
  for (const b of valid) {
    const span = (((b.endDayOfWeek as number) - (b.startDayOfWeek as number)) % 7 + 7) % 7;
    const key = `${b.startTime}|${b.endTime}|${span}`;
    const g = groups.get(key);
    if (g) {
      g.startDays.push(b.startDayOfWeek as number);
    } else {
      groups.set(key, {
        startDays: [b.startDayOfWeek as number],
        startTime: b.startTime as number,
        endTime: b.endTime as number,
      });
    }
  }

  const parts = Array.from(groups.values()).map(
    (g) =>
      `${formatKstDays(g.startDays)} · ${formatKstTimeOfDay(g.startTime)} – ${formatKstTimeOfDay(g.endTime)}`
  );
  return parts.join('; ') + zoneSuffixIfForeign(zoneId, now);
}
